/**
 * Modal de sélection du métier
 * Affiché quand l'utilisateur skip l'onboarding pour s'assurer
 * que le métier est défini (nécessaire pour les benchmarks)
 */

import { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Briefcase, MapPin, Loader2,
  Droplets, Zap, BrickWall, Home, PaintBucket, Grid3X3, DoorOpen,
  Tractor, TreeDeciduous, Building2, Key, Snowflake, HelpCircle, Wrench,
  Thermometer, Hammer, MoreHorizontal
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  CATEGORIES_METIERS,
  BENCHMARKS_METIERS,
} from '@/data/benchmarkMetiers';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

// Mapping des icônes Lucide par nom
const ICON_MAP: Record<string, LucideIcon> = {
  Droplets,
  Zap,
  Brick: BrickWall,
  Home,
  PaintBucket,
  Grid3x3: Grid3X3,
  DoorOpen,
  Tractor,
  TreeDeciduous,
  Building2,
  Key,
  Snowflake,
  HelpCircle,
  Wrench,
  Thermometer,
  Hammer,
  MoreHorizontal,
};

interface MetierSelectionModalProps {
  open: boolean;
  onComplete: () => void;
}

export function MetierSelectionModal({ open, onComplete }: MetierSelectionModalProps) {
  const { toast } = useToast();
  const [selectedCategorie, setSelectedCategorie] = useState<string>('entreprise_batiment');
  const [selectedMetier, setSelectedMetier] = useState<string>('');
  const [localisationType, setLocalisationType] = useState<'province' | 'metropole'>('province');
  const [isSaving, setIsSaving] = useState(false);

  // Filtrer les métiers par catégorie sélectionnée
  const metiersFiltres = useMemo(() => {
    if (!selectedCategorie) return [];
    return BENCHMARKS_METIERS.filter(m => m.categorie_key === selectedCategorie);
  }, [selectedCategorie]);

  // Sélectionner automatiquement le premier métier quand la catégorie change
  const handleCategorieChange = (categorieKey: string) => {
    setSelectedCategorie(categorieKey);
    const metiers = BENCHMARKS_METIERS.filter(m => m.categorie_key === categorieKey);
    if (metiers.length > 0) {
      setSelectedMetier(metiers[0].metier_key);
    } else {
      setSelectedMetier('');
    }
  };

  // Initialiser avec le premier métier de la première catégorie
  useState(() => {
    const metiers = BENCHMARKS_METIERS.filter(m => m.categorie_key === 'entreprise_batiment');
    if (metiers.length > 0 && !selectedMetier) {
      setSelectedMetier(metiers[0].metier_key);
    }
  });

  // Sauvegarder le métier et continuer
  const handleSave = async () => {
    if (!selectedMetier) {
      toast({
        variant: 'destructive',
        title: 'Métier requis',
        description: 'Veuillez sélectionner votre métier.',
      });
      return;
    }

    setIsSaving(true);
    try {
      // Récupérer l'utilisateur connecté
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Non connecté');

      // Récupérer l'entreprise de l'utilisateur (maybeSingle pour éviter erreur 406 si pas trouvé)
      const { data: entreprise } = await supabase
        .from('entreprises')
        .select('id')
        .eq('proprietaire_user_id', user.id)
        .maybeSingle();

      if (!entreprise) {
        // Créer l'entreprise si elle n'existe pas
        const nomEntreprise = user.user_metadata?.entreprise ||
                              `Entreprise de ${user.user_metadata?.prenom || user.email?.split('@')[0] || 'Mon'}`;

        const { error: createError } = await supabase
          .from('entreprises')
          .insert({
            proprietaire_user_id: user.id,
            nom: nomEntreprise,
            metier_key: selectedMetier,
            localisation_type: localisationType,
          });

        if (createError) {
          console.error('Erreur création entreprise:', createError);
          throw createError;
        }
      } else {
        // Mettre à jour le métier de l'entreprise existante
        const { error: updateError } = await supabase
          .from('entreprises')
          .update({
            metier_key: selectedMetier,
            localisation_type: localisationType,
          })
          .eq('id', entreprise.id);

        if (updateError) {
          console.error('Erreur mise à jour entreprise:', updateError);
          throw updateError;
        }
      }

      toast({
        title: 'Métier enregistré',
        description: 'Vos analyses seront personnalisées selon votre métier.',
      });

      onComplete();
    } catch (error) {
      console.error('Erreur sauvegarde métier:', error);
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: 'Impossible de sauvegarder votre métier.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        variant="industrial"
        className="sm:max-w-md [&>button:last-child]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-primary" />
            Quel est votre métier ?
          </DialogTitle>
          <DialogDescription>
            Cette information est nécessaire pour personnaliser vos analyses de rentabilité.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Sélection de la catégorie */}
          <div className="space-y-2">
            <Label>Catégorie de métier</Label>
            <Select
              value={selectedCategorie}
              onValueChange={handleCategorieChange}
            >
              <SelectTrigger>
                <SelectValue placeholder="Choisissez une catégorie..." />
              </SelectTrigger>
              <SelectContent className="max-h-[200px]">
                {CATEGORIES_METIERS.map((cat) => {
                  const IconComponent = ICON_MAP[cat.icon] || HelpCircle;
                  return (
                    <SelectItem key={cat.categorie_key} value={cat.categorie_key}>
                      <span className="flex items-center gap-2">
                        <IconComponent className="w-4 h-4" />
                        <span>{cat.categorie_name}</span>
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {/* Sélection du métier */}
          {selectedCategorie && metiersFiltres.length > 0 && (
            <div className="space-y-2">
              <Label>Votre métier</Label>
              <Select
                value={selectedMetier}
                onValueChange={setSelectedMetier}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choisissez votre métier..." />
                </SelectTrigger>
                <SelectContent className="max-h-[200px]">
                  {metiersFiltres.map((metier) => (
                    <SelectItem key={metier.metier_key} value={metier.metier_key}>
                      {metier.metier_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Localisation */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Votre localisation
            </Label>
            <RadioGroup
              value={localisationType}
              onValueChange={(value: 'province' | 'metropole') => setLocalisationType(value)}
              className="flex gap-3"
            >
              <div
                className={`flex-1 flex items-center space-x-2 p-3 rounded-lg border transition-colors cursor-pointer ${
                  localisationType === 'province'
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50'
                }`}
                onClick={() => setLocalisationType('province')}
              >
                <RadioGroupItem value="province" id="modal-province" />
                <label htmlFor="modal-province" className="cursor-pointer text-sm">
                  <span className="font-medium">Province</span>
                </label>
              </div>
              <div
                className={`flex-1 flex items-center space-x-2 p-3 rounded-lg border transition-colors cursor-pointer ${
                  localisationType === 'metropole'
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50'
                }`}
                onClick={() => setLocalisationType('metropole')}
              >
                <RadioGroupItem value="metropole" id="modal-metropole" />
                <label htmlFor="modal-metropole" className="cursor-pointer text-sm">
                  <span className="font-medium">IDF / Métropole</span>
                </label>
              </div>
            </RadioGroup>
          </div>
        </div>

        <Button
          onClick={handleSave}
          disabled={!selectedMetier || isSaving}
          className="w-full bg-gradient-to-r from-[#F7B500] to-[#FF9500] text-black font-bold hover:brightness-110 shadow-[0_6px_20px_rgba(247,181,0,0.2)]"
        >
          {isSaving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Enregistrement...
            </>
          ) : (
            'Continuer'
          )}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

export default MetierSelectionModal;
